import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useGetDraftsQuery } from "@/store/api/draftsApi";
import { useDeletePostMutation } from "@/store/api/postApi";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { IPost } from "@/store/api/postApiData";
import DraftItem from "./DraftItem";
import BasketSvg from "@/assets/svg/BasketSvg";
import DeleteModal from "@/components/UI/modal/DeleteModal/DeleteModal";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import CreateLongPostModal from "@/components/UI/modal/CreateLongPostModal/CreateLongPostModal";
import CreatePostModal from "@/components/UI/modal/CreatePostModal/CreatePostModal";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useLocation, useNavigate } from "react-router-dom";
import theme from "@/theme";

const DRAFTS_LIMIT = 20;

const Drafts = () => {
  const [allDrafts, setAllDrafts] = useState<IPost[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const tabs: ("Posts" | "Articles")[] = ["Posts", "Articles"];
  const [skipQuery, setSkipQuery] = useState(false);
  const [isSwitchingTab, setIsSwitchingTab] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<IPost | null>(null);
  const [toast, setToast] = useState<{ message: string; success?: boolean } | null>(null);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [isLongPostModalOpen, setIsLongPostModalOpen] = useState(false);
  const draftLoading = useAppSelector((state) => state.loading.draftLoading);
  const draftLoadingType = useAppSelector((state) => state.loading.draftLoadingType);
  const prevDraftLoading = useRef(draftLoading);
  // const [refreshingDrafts, setRefreshingDrafts] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const typeParam = searchParams.get("type");

  const activeTab: "Posts" | "Articles" = typeParam === "Articles" ? "Articles" : "Posts";
  const activeType = activeTab.toLowerCase();
  const startedLoadingType = useRef<string | null>(null);
  const [isRefreshingCurrentTab, setIsRefreshingCurrentTab] = useState(false);
  const shouldShowDraftLoader =
    (draftLoading && draftLoadingType === activeType) || isRefreshingCurrentTab;
  const isCreatingCurrentTabDraft =
    (draftLoading && startedLoadingType.current === activeType) || isRefreshingCurrentTab;

  const {
    data: drafts,
    isFetching,
    isLoading,
    refetch,
  } = useGetDraftsQuery(
    {
      page,
      limit: DRAFTS_LIMIT,
      type: activeTab.toLowerCase(),
    },
    { skip: skipQuery }
  );

  const [deletePost] = useDeletePostMutation();

  const [selectedDrafts, setSelectedDrafts] = useState<string[]>([]);

  const toggleDraft = (uid: string) => {
    setSelectedDrafts((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  // const extractTitle = (html: string): string | null => {
  //   if (!html) return null;
  //   const div = document.createElement("div");
  //   div.innerHTML = html;

  //   const heading = div.querySelector("h1, h2");
  //   return heading?.textContent?.trim() || null;
  // };

  const extractTitleAndText = (html: string): { title: string | null; text: string } => {
    if (!html) return { title: null, text: "" };

    const div = document.createElement("div");
    div.innerHTML = html;

    const heading = div.querySelector("h1, h2");
    let title: string | null = null;

    if (heading) {
      title = heading.textContent?.trim() || null;
      heading.remove();
    }

    const text = div.textContent?.replace(/\s+/g, " ").trim() || "";
    return { title, text };
  };

  const handleDraftClick = (post: IPost) => {
    setSelectedDraft(post);
    if (activeTab === "Posts") {
      setIsPostModalOpen(true);
    } else {
      setIsLongPostModalOpen(true);
    }
  };

  useEffect(() => {
    if (!drafts) return;

    if (page === 1) {
      setAllDrafts(drafts);
    } else {
      setAllDrafts((prev) => [
        ...prev,
        ...drafts.filter((d) => !prev.some((ad) => ad.uid === d.uid)),
      ]);
    }

    setHasMore(drafts.length === DRAFTS_LIMIT);
    setIsSwitchingTab(false);
  }, [drafts, page]);

  const isFetchingRef = useRef(isFetching);
  const isLoadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);
  const allDraftsLengthRef = useRef(allDrafts.length);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    allDraftsLengthRef.current = allDrafts.length;
  }, [allDrafts.length]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        hasMoreRef.current &&
        !isFetchingRef.current &&
        !isLoadingRef.current &&
        allDraftsLengthRef.current > 0
      ) {
        setPage((prev) => prev + 1);
      }
    });

    observer.current.observe(loadMoreRef.current);

    return () => observer.current?.disconnect();
  }, []);

  const extractText = (html: string): string => {
    if (!html) return "";

    const div = document.createElement("div");
    div.innerHTML = html;

    const text = div.textContent || div.innerText || "";

    return text.replace(/\s+/g, " ").trim();
  };
  // const extractFirstImage = (html: string): string | null => {
  //   if (!html) return null;

  //   const div = document.createElement("div");
  //   div.innerHTML = html;

  //   const img = div.querySelector("img");
  //   return img?.getAttribute("src") || null;
  // };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    const draftsToDelete = [...selectedDrafts];

    setAllDrafts((prev) => prev.filter((d) => !draftsToDelete.includes(d.uid)));
    setSelectedDrafts([]);
    setOpenDeleteModal(false);
    const deletePromises = draftsToDelete.map((uid) => deletePost(uid).unwrap());

    try {
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Failed to delete one or more drafts:", error);
    }
  };

  useEffect(() => {
    setAllDrafts([]);
    setSelectedDrafts([]);
    setPage(1);
    setHasMore(true);
    setIsSwitchingTab(true);
    setSkipQuery(true);
    const timer = setTimeout(() => setSkipQuery(false), 0);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const getTabText = (tab: string, count: number) => {
    const base = tab.toLowerCase().endsWith("s")
      ? tab.toLowerCase().slice(0, -1)
      : tab.toLowerCase();
    return `${base}${count > 1 ? "s" : ""}`;
  };

  useEffect(() => {
    if (!prevDraftLoading.current && draftLoading) {
      startedLoadingType.current = draftLoadingType;

      if (draftLoadingType === activeType) {
        setIsRefreshingCurrentTab(true);
      }
    }

    const finishedCurrentTabDraft =
      prevDraftLoading.current && !draftLoading && startedLoadingType.current === activeType;

    if (finishedCurrentTabDraft) {
      setPage(1);
      refetch().finally(() => {
        setIsRefreshingCurrentTab(false);
        startedLoadingType.current = null;
      });
    }

    prevDraftLoading.current = draftLoading;
  }, [draftLoading, draftLoadingType, activeType, refetch]);

  const [showInitialLoader, setShowInitialLoader] = useState(true);

  useEffect(() => {
    if (isSwitchingTab || isLoading || isRefreshingCurrentTab) {
      setShowInitialLoader(true);
    } else {
      setShowInitialLoader(false);
    }
  }, [isSwitchingTab, isLoading]);

  const shouldShowFullHeightLoader =
    (isSwitchingTab || isLoading || isRefreshingCurrentTab) && showInitialLoader;

  return (
    <Box
      sx={{
        height: "100%",
        backgroundColor: "#fff",
        boxSizing: "border-box",
      }}
    >
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      {isLongPostModalOpen && (
        <CreateLongPostModal
          open={isLongPostModalOpen}
          setIsLongPostModalOpen={setIsLongPostModalOpen}
          editPost={selectedDraft}
          isDraft
          setToast={setToast}
        />
      )}
      {isPostModalOpen && (
        <CreatePostModal
          open={isPostModalOpen}
          handleClose={() => {
            setIsPostModalOpen(false);
          }}
          editPost={selectedDraft}
          isDraft
          postVariant={
            selectedDraft?.topic
              ? selectedDraft.topic.charAt(0).toUpperCase() +
                selectedDraft.topic.slice(1).toLowerCase()
              : ""
          }
          setToast={setToast}
        />
      )}
      <DeleteModal
        handleClose={handleCloseDeleteModal}
        open={openDeleteModal}
        onDelete={handleConfirmDelete}
        width={263}
        height={198}
        iconSize={36}
        iconInnerSize={24}
        title={`Are you sure you want to delete ${
          selectedDrafts.length === 1 ? "this" : selectedDrafts.length
        } ${getTabText(activeTab, selectedDrafts.length)} drafts?`}
        description="You won't be able to recover it"
        variant="post"
        sxWrapper={{
          padding: "10px 0px",
          borderRadius: "16px",
          bgcolor: "#FFFFFFE5",
          backdropFilter: "blur(4px)",
          boxShadow: "0px 32px 48px -8px #0000001A, 0px 0px 14px -4px #0000000D",
          justifyContent: "space-between",
        }}
        sxIconWrapper={{
          padding: "9px",
          marginTop: "0",
        }}
        sxTitle={{
          maxWidth: "209px",
          fontFamily: "Inter",
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "20px",
          letterSpacing: "-0.02em",
          color: "#1A1D1F",
          whiteSpace: "normal",
          margin: "12px 0 4px 0",
        }}
        sxDescription={{
          fontFamily: "Inter",
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "24px",
          letterSpacing: "-0.015em",
          color: "#6F767E",
        }}
        sxButtonWrapper={{
          height: "48px",
          marginTop: "auto",
          width: "100%",
          gap: "23px",
        }}
        sxCancelButton={{
          width: "120px",
          minWidth: "100px",
          height: "48px",
          fontFamily: "Inter",
          fontWeight: "700",
          fontSize: "15px",
          lineHeight: "24px",
          letterSpacing: "-1%",
          "&:hover": {
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
        sxDeleteButton={{
          minWidth: "100px",
          height: "48px",
          width: "120px",
          fontFamily: "Inter",
          fontWeight: "700",
          fontSize: "15px",
          lineHeight: "24px",
          letterSpacing: "-1%",
          bgcolor: "none",
          "&:hover": {
            bgcolor: "transparent",
            boxShadow: "none",
          },
        }}
        sxCloseBtn={{
          display: "none",
        }}
      />

      <Box
        sx={{
          // width: "1160px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            width: "100%",
            height: "54px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px",
            borderBottom: "1px solid #F4F5F6",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              fontSize: "24px",
              fontWeight: 600,
              lineHeight: "130%",
              color: "#2A2D31",
            }}
          >
            Drafts
          </Typography>

          {/* TABS */}
          <Box
            sx={{
              width: "143px",
              display: "flex",
              gap: "40px",
            }}
          >
            {tabs.map((tab) => {
              const active = tab === activeTab;
              return (
                <Typography
                  key={tab}
                  onClick={() => {
                    if (tab === "Posts") {
                      navigate("/drafts", { replace: true });
                    } else {
                      navigate("/drafts?type=Articles", { replace: true });
                    }
                  }}
                  sx={{
                    height: "24px",
                    cursor: "pointer",
                    fontFamily: "Inter",
                    fontWeight: active ? 600 : 500,
                    fontSize: 16,
                    lineHeight: "20px",
                    color: active ? theme.palette.custom.mainBlue : "#52575D",
                    borderBottom: active ? `2px solid ${theme.palette.custom.mainBlue}` : "none",
                    pb: "2px",
                  }}
                >
                  {tab}
                </Typography>
              );
            })}
          </Box>
        </Box>

        {/* CONTENT BLOCK */}
        <Box
          sx={{
            minHeight: "calc(100vh - 200px)",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "0 20px",
            position: "relative",
          }}
        >
          {/* Loader */}
          {shouldShowFullHeightLoader && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CustomLoader />
            </Box>
          )}

          {shouldShowDraftLoader && allDrafts.length !== 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: 2,
                minHeight: "50px",
              }}
            >
              <CustomLoader />
            </Box>
          )}

          {/* Message if there are no drafts */}
          {!allDrafts.length &&
            !isLoading &&
            !isFetching &&
            !isSwitchingTab &&
            !isCreatingCurrentTabDraft && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  gap: 1,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: activeTab === "Posts" ? 341 : 353,
                    height: 49,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      width: activeTab === "Posts" ? "232px" : "247px",
                      height: "25px",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      fontSize: 16,
                      lineHeight: "155%",
                      color: "#7D8690",
                    }}
                  >
                    You don’t have any {activeTab.toLowerCase().slice(0, -1)} drafts
                  </Typography>
                  <Typography
                    sx={{
                      width: activeTab === "Posts" ? "341px" : "353px",
                      height: "20px",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: "145%",
                      color: "#C6CACE",
                    }}
                  >
                    Write your {activeTab.toLowerCase().slice(0, -1)} and save it as a draft to see
                    it here.
                  </Typography>
                </Box>
              </Box>
            )}

          {/* Draft list */}
          {allDrafts.length > 0 &&
            allDrafts.map((draft) => {
              const image = draft.attachments?.[0]?.url;
              const { title, text } =
                activeTab === "Articles"
                  ? extractTitleAndText(draft.content)
                  : { title: undefined, text: extractText(draft.content) };
              return (
                <DraftItem
                  key={draft.uid}
                  text={text}
                  image={image}
                  checked={selectedDrafts.includes(draft.uid)}
                  onCheck={() => toggleDraft(draft.uid)}
                  activeTab={activeTab}
                  onClick={() => handleDraftClick(draft)}
                  title={title}
                />
              );
            })}

          {/* Loader for infinite scroll */}
          {isFetching && page > 1 && hasMore && (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
              <CustomLoader />
            </Box>
          )}
          <div ref={loadMoreRef} style={{ height: "50px" }} />
        </Box>

        {/* {!hasMore && allDrafts.length > 0 && (
          <Box sx={{ textAlign: "center", padding: 2, color: "text.secondary" }}>
            No more drafts
          </Box>
        )} */}
      </Box>

      {selectedDrafts.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 40,
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: "300px",
            height: 52,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 0px 16px 0px #0000001A",
            display: "flex",
            overflow: "hidden",
            padding: "8px ",
          }}
        >
          {/* Left block */}
          <Box
            sx={{
              minWidth: "145px",
              maxWidth: "300px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              padding: "8px 14px 8px 8px",
              borderRight: "1px solid #F4F5F6",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Typography
              sx={{
                width: "16px",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "20px",
                color: "#2A2D31",
                textAlign: "right",
              }}
            >
              {selectedDrafts.length}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: 16,
                lineHeight: "20px",
                color: "#2A2D31",
              }}
            >
              {getTabText(activeTab, selectedDrafts.length)} selected
            </Typography>
          </Box>

          {/* Right block */}
          <Box
            sx={{
              width: 96,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              padding: "8px 14px",
              cursor: "pointer",
            }}
            onClick={() => {
              setOpenDeleteModal(true);
            }}
          >
            <BasketSvg width={20} height={20} />
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: 14,
                lineHeight: "20px",
                color: "#CE212B",
              }}
            >
              Delete
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Drafts;
